/**
 * Pure, framework-free filtering helpers for the Admin Progress Notes list.
 * Status model (from backend routes/notes.py): note.status is only DRAFT or
 * SUBMITTED; a struck note keeps its status plus a `struck` object (there is NO
 * distinct AMENDED status — amendments live in note.amendments[]). Follow-up is
 * the stored boolean note.requires_follow_up. Date filtering uses note.created_at
 * (the field the list already displays), compared as inclusive calendar days.
 */

export const NOTE_TEMPLATES = [
  ['DAILY_SUPPORT', 'General Update / Daily Support'],
  ['COMMUNITY_ACCESS', 'Social & Community Access'],
  ['PERSONAL_CARE', 'Personal Care'],
  ['BEHAVIOUR_SUPPORT', 'Behaviour Support'],
  ['ROUTINE', 'Routine'],
  ['CLINICAL', 'Clinical'],
  ['INCIDENT', 'Incident'],
  ['RESTRICTIVE_PRACTICE', 'Restrictive Practice'],
  ['OTHER', 'Other'],
];

export const NOTE_STATUSES = [
  ['DRAFT', 'Draft'],
  ['SUBMITTED', 'Submitted'],
  ['STRUCK', 'Struck'],
];

export const DEFAULT_NOTE_FILTERS = { status: 'ALL', template: 'ALL', from: '', to: '', followUp: 'ALL', author: 'ALL' };

export const UNKNOWN_AUTHOR = '__UNKNOWN__';

const day = (v) => (v ? String(v).slice(0, 10) : '');

/** Canonical ORIGINAL note author identity (never amendment/strike actor). */
export function noteAuthorKey(n) {
  return n.author_user_id || UNKNOWN_AUTHOR;
}

function noteAuthorLabel(n) {
  const name = (n.author_name || '').trim();
  return name || 'Unknown';
}

/**
 * Unique original-author options from the full note set. De-duped by author ID
 * (identity-safe: two staff with the same name stay distinct), sorted A→Z by
 * label with 'Unknown' last.
 */
export function noteAuthorOptions(notes) {
  const map = new Map();
  (notes || []).forEach((n) => {
    const key = noteAuthorKey(n);
    if (!map.has(key)) map.set(key, noteAuthorLabel(n));
    else if (map.get(key) === 'Unknown') map.set(key, noteAuthorLabel(n)); // upgrade if a later note has a name
  });
  return Array.from(map.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => {
      if (a.value === UNKNOWN_AUTHOR) return 1;
      if (b.value === UNKNOWN_AUTHOR) return -1;
      return a.label.localeCompare(b.label);
    });
}

/** Real timestamp-based sort applied AFTER filtering. Invalid/missing dates last. */
export function sortProgressNotes(notes, direction = 'NEWEST') {
  const ts = (n) => {
    const t = n && n.created_at ? Date.parse(n.created_at) : NaN;
    return Number.isNaN(t) ? null : t;
  };
  return [...(notes || [])].sort((a, b) => {
    const ta = ts(a), tb = ts(b);
    if (ta === null && tb === null) return 0;
    if (ta === null) return 1;  // unknown-date notes last (both directions)
    if (tb === null) return -1;
    return direction === 'OLDEST' ? ta - tb : tb - ta;
  });
}

/** True when both dates are set and From is after To. */
export function isDateRangeInvalid(filters) {
  return !!(filters.from && filters.to && filters.from > filters.to);
}

/** Number of active (non-default) filter categories. Date range counts once. Sort is NOT a filter. */
export function activeNoteFilterCount(filters) {
  let n = 0;
  if (filters.status && filters.status !== 'ALL') n += 1;
  if (filters.template && filters.template !== 'ALL') n += 1;
  if (filters.from || filters.to) n += 1;
  if (filters.followUp && filters.followUp !== 'ALL') n += 1;
  if (filters.author && filters.author !== 'ALL') n += 1;
  return n;
}

/** AND semantics across categories. */
export function filterProgressNotes(notes, filters) {
  const f = { ...DEFAULT_NOTE_FILTERS, ...(filters || {}) };
  return (notes || []).filter((n) => {
    // Status
    if (f.status === 'DRAFT' && n.status !== 'DRAFT') return false;
    if (f.status === 'SUBMITTED' && n.status !== 'SUBMITTED') return false;
    if (f.status === 'STRUCK' && !n.struck) return false;
    // Template / type
    if (f.template !== 'ALL' && (n.template_key || n.note_type) !== f.template) return false;
    // Date range (inclusive, by created_at calendar day)
    const d = day(n.created_at);
    if (f.from && (!d || d < f.from)) return false;
    if (f.to && (!d || d > f.to)) return false;
    // Requires follow-up
    if (f.followUp === 'YES' && n.requires_follow_up !== true) return false;
    if (f.followUp === 'NO' && n.requires_follow_up === true) return false;
    // Original author / recorded-by (never amendment/strike actor)
    if (f.author !== 'ALL' && noteAuthorKey(n) !== f.author) return false;
    return true;
  });
}
